import { LoaderLayout } from '@app/components';
import { LazyRenderer } from '@app/components/rich-text/lazy-renderer';
import {
  cleanHtmlForClipboard,
  extractPlainTextFromHtml,
} from '@app/routes/content/routes/recordings/utils';
import { useAppDispatch, useAppSelector } from '@app/store';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useCreatorLabAnalytics } from '../../../routes/lab/hooks';
import { ContentHistoryItem } from '../domain';
import { useContentCardContent, useContentComposerFeedback } from '../hooks';
import {
  selectCanRegenerateContent,
  selectIsContentGenerationBlockedForType,
} from '../selector';
import {
  ContentCardFooter,
  ContentErrorState,
  ContentOptionTabs,
  ContentTagPills,
} from './';
import { ContentTagPillsPlaceholder } from './content-tag-pills';

type ContentLoadingPlaceholderProps = {
  /** Whether AI is generating content - shows additional message when true */
  isGenerating?: boolean;
};

/**
 * Content loading placeholder - shown while content is being fetched or generated.
 * When isGenerating is true, shows a message indicating content is being generated.
 */
const ContentLoadingPlaceholder: React.FC<ContentLoadingPlaceholderProps> = ({
  isGenerating = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      {isGenerating && (
        <p className="text-sm text-white/60">
          {t('content-recording.content-composer.generated-content.generating')}
        </p>
      )}
      <div className="flex flex-col gap-0.5">
        <div className="w-full h-5 bg-spring-neutral-b4 rounded-[10px] animate-pulse" />
        <div className="w-full h-5 bg-spring-neutral-b4 rounded-[10px] animate-pulse" />
        <div className="w-3/4 h-5 bg-spring-neutral-b4 rounded-[10px] animate-pulse" />
        <div className="w-full h-5 bg-spring-neutral-b4 rounded-[10px] animate-pulse mt-2" />
        <div className="w-3/4 h-5 bg-spring-neutral-b4 rounded-[10px] animate-pulse" />
      </div>
    </div>
  );
};

export type GeneratedContentCardProps = {
  item: ContentHistoryItem;
  className?: string;
};

export const GeneratedContentCard: React.FC<GeneratedContentCardProps> = ({
  item,
  className = '',
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(0);

  // Destructure item for easier access
  const { id, status, contentType, toneOfVoice, topics, context } = item;

  // Use hook to manage content fetching with lazy loading
  // Pass generation status so hook knows when content is ready to fetch
  const { ref, content, isLoading, isGenerating, isError, retry } =
    useContentCardContent(id, status);

  // Check if pills data is available (optional fields from enrichment)
  const hasPillsData = toneOfVoice && topics && topics.length > 0;

  // Check if generation is blocked for this content type (to disable regenerate button)
  const isRegenerating = useAppSelector(state =>
    selectIsContentGenerationBlockedForType(state, contentType),
  );

  // Check if content can be regenerated (attributes still exist in config)
  const canRegenerate = useAppSelector(state =>
    selectCanRegenerateContent(
      state,
      topics?.[0],
      toneOfVoice,
      contentType,
      context?.recipientTypeId,
      context?.emailTypeId,
    ),
  );

  // Determine option count based on content type (SOCIAL_POST has 2 options, others have 1)
  const optionCount = contentType === 'SOCIAL_POST' ? 2 : 1;

  // Content is available when we have fetched data with options
  const hasContent = content && content.options && content.options.length > 0;
  const currentContent = hasContent
    ? content.options[selectedOptionIndex] ?? content.options[0]
    : null;

  // Wire up feedback hook for the currently selected option
  const feedbackHook = useContentComposerFeedback(
    contentType,
    id,
    selectedOptionIndex,
  );

  const { trackContentCopied, trackContentRegenerated } =
    useCreatorLabAnalytics();

  const getOptionType = useCallback((): 'option_1' | 'option_2' | undefined => {
    if (contentType !== 'SOCIAL_POST') return undefined;
    return selectedOptionIndex === 0 ? 'option_1' : 'option_2';
  }, [contentType, selectedOptionIndex]);

  const handleCopySuccess = useCallback(() => {
    trackContentCopied(getOptionType());
  }, [trackContentCopied, getOptionType]);

  const handleRegenerate = useCallback(() => {
    trackContentRegenerated();

    dispatch({
      type: '[content-composer] regenerate content',
      payload: {
        contentType,
        toneOfVoiceId: toneOfVoice || '',
        topicId: topics?.[0] || '', // Use first topic from the content
        ...(contentType === 'EMAIL' &&
          context && {
            emailAudienceId: context.recipientTypeId,
            emailTypeId: context.emailTypeId,
          }),
      },
    });
  }, [
    dispatch,
    contentType,
    toneOfVoice,
    topics,
    context,
    trackContentRegenerated,
  ]);

  // Determine footer visibility and mode based on status
  // INITIAL/IN_PROGRESS: hide footer (use isGenerating from hook)
  // FINISHED: show full footer
  // FAILED: show regenerate-only footer
  const isGenerationFailed = status === 'FAILED';
  const showFooter = !isGenerating && (!isGenerationFailed || canRegenerate);

  const getFooterMode = ():
    | 'full'
    | 'copy-feedback-only'
    | 'regenerate-only' => {
    if (isGenerationFailed) return 'regenerate-only';
    if (!canRegenerate) return 'copy-feedback-only';
    return 'full';
  };
  const footerMode = getFooterMode();

  const contentContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const contentContainer = contentContainerRef.current;
    if (!contentContainer || !hasContent || !currentContent) {
      return;
    }

    const handleCopyEvent = (event: ClipboardEvent) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return;
      }

      const range = selection.getRangeAt(0);
      const container = document.createElement('div');
      container.appendChild(range.cloneContents());
      const selectedHtml = container.innerHTML;

      if (!selectedHtml.trim()) {
        return;
      }

      const cleanedHtml = cleanHtmlForClipboard(selectedHtml);
      const plainText = extractPlainTextFromHtml(cleanedHtml);

      event.clipboardData?.setData('text/html', cleanedHtml);
      event.clipboardData?.setData('text/plain', plainText);
      event.preventDefault();

      container.remove();
    };

    contentContainer.addEventListener('copy', handleCopyEvent);

    return () => {
      contentContainer.removeEventListener('copy', handleCopyEvent);
    };
  }, [hasContent, currentContent]);

  return (
    <div
      ref={ref}
      className={cx(
        'flex flex-col gap-4 p-4 border border-white/20 rounded-[10px] bg-spring-neutral-base',
        className,
      )}
      data-testid="generated-content-card"
    >
      {/* Tag Pills - only show if enrichment data is available */}
      {hasPillsData ? (
        <ContentTagPills
          contentType={contentType}
          toneOfVoice={toneOfVoice}
          topicId={topics[0]}
          recipientTypeId={context?.recipientTypeId}
          emailTypeId={context?.emailTypeId}
        />
      ) : (
        <ContentTagPillsPlaceholder />
      )}

      {/* Option Tabs - only show for SOCIAL_POST when content is loaded */}
      {optionCount > 1 && hasContent && (
        <ContentOptionTabs
          count={optionCount}
          selectedOptionIndex={selectedOptionIndex}
          onOptionChange={setSelectedOptionIndex}
        />
      )}

      {/* Content Area */}
      <div ref={contentContainerRef} className="flex flex-col gap-2">
        {isGenerationFailed ? (
          // Generation failed - show error state with LoaderLayout
          <LoaderLayout
            title={t(
              'content-recording.content-composer.generated-content.generation-failed-title',
            )}
            description={t(
              'content-recording.content-composer.generated-content.generation-failed-description',
            )}
          />
        ) : isError ? (
          // Fetch error - content was generated but failed to load
          <ContentErrorState onRetry={retry} />
        ) : isLoading ? (
          <ContentLoadingPlaceholder isGenerating={isGenerating} />
        ) : hasContent && currentContent ? (
          <LazyRenderer value={currentContent} />
        ) : (
          <ContentLoadingPlaceholder />
        )}
      </div>

      {/* Footer - hide during generation, show regenerate-only for failed */}
      {showFooter && (
        <ContentCardFooter
          contentToCopy={currentContent ?? ''}
          onCopySuccess={handleCopySuccess}
          selectedFeedback={feedbackHook.selectedFeedback}
          onFeedbackSubmit={feedbackHook.handleFeedbackSubmit}
          onFeedbackChange={feedbackHook.handleFeedbackChange}
          onThumbsDownClick={feedbackHook.handleThumbsDownClick}
          isCopyDisabled={!hasContent}
          onRegenerate={handleRegenerate}
          isRegenerating={isRegenerating}
          mode={footerMode}
          showFeedback={feedbackHook.shouldShowFeedback}
        />
      )}
    </div>
  );
};

export const GeneratedContentCardPlaceholder = () => {
  return (
    <div
      className="flex flex-col gap-4 p-4 border border-white/20 rounded-[10px] bg-spring-neutral-base"
      data-testid="generated-content-card-placeholder"
    >
      <ContentTagPillsPlaceholder />
      <div className="flex flex-col gap-0.5">
        <div className="w-full h-5 bg-spring-neutral-b4 rounded-[10px] animate-pulse" />
        <div className="w-full h-5 bg-spring-neutral-b4 rounded-[10px] animate-pulse" />
        <div className="w-3/4 h-5 bg-spring-neutral-b4 rounded-[10px] animate-pulse" />
        <div className="w-full h-5 bg-spring-neutral-b4 rounded-[10px] animate-pulse mt-2" />
        <div className="w-3/4 h-5 bg-spring-neutral-b4 rounded-[10px] animate-pulse" />
      </div>
    </div>
  );
};
